import {
  openModal,
  routeTo,
  routeBack,
  finish,
  partiallyUpdate
} from '../../../core/actions';
import FlowHeader from '../../../components/FlowHeader/FlowHeader';
import FooterSite from '../../../components/FooterSite/FooterSite';
import BaseLayout from '../../../components/BaseLayout/BaseLayout';
import SummitRewards from '../../../components/BaseLayout/SummitRewards';
import {
  hasURLTickers,
  isValidEmail,
  isValidMaskedSSN,
  usernameValidation,
  passwordValidation,
  isValidDob,
  meetsMinorAgeValidation,
  meetsMinimumInvestment,
  isReviewMode,
  chose,
  chosenAddressValidation,
  existingUserShouldGotoLogin,
  existingUserShouldGotoEnablement,
  isUsernameAlreadyTaken,
  choseResultingPath,
  userValidationFailed,
  isAuthenticated,
  isUniqueSSN,
  isValidSSN,
  compose,
  guestUserLoginRequired,
  showUpdateToIClassModal,
  existingAgreementsPresent
} from '../../shared/testers';

import TaxableProfileScreen from '../../../screens/ProfileScreen/TaxableProfileScreen';
import PersonalInformationScreen from '../../../screens/PersonalInformationScreen/PersonalInformationScreen';
import CreateCredentialsScreen from '../../../screens/CreateCredentialsScreen/CreateCredentialsScreen';
import ContactInformationScreen from '../../../screens/ContactInformationScreen/ContactInformationScreen';
import ReviewScreen from '../../../screens/ReviewScreen/ReviewScreen';
import ConfirmationScreen from '../../../screens/ConfirmationScreen/ConfirmationScreen';
import InvestmentOptionsScreen from '../../../screens/InvestmentOptionsScreen/InvestmentOptionsScreen';
import InvestmentReviewScreen from '../../../screens/InvestmentReviewScreen/InvestmentReviewScreen';
import BYOPScreen from '../../../screens/BYOPScreen/BYOPScreen';
import MoneyMarketScreen from '../../../screens/MoneyMarketScreen/MoneyMarketScreen';
import TaxableChecklistScreen from '../../../screens/ChecklistScreen/TaxableChecklistScreen';
import InvestmentOptionsContents from '../../../components/InvestmentOptions/TaxableContents';
import UserValidationModal from '../../../components/UserValidationModal/UserValidationModal';
import makeForcedLoginModal from '../../../components/ForcedLoginModal/ForcedLoginModal';
import GuestUserLoginModal from '../../../components/GuestUserLoginModal/GuestUserLoginModal';
import AddressValidationModal from '../../../components/AddressValidationModal/AddressValidationModal';
import CredentialsValidationModal from '../../../components/CredentialsValidationModal/CredentialsValidationModal';
import UpdateToIClassModal from '../../../components/UpdateToIClass/UpdateToIClassModal';

import {
  commitPersonalInformation,
  commitContactInformation,
  createUGMAUTMAApplication,
  makeLoadProfileData,
  updateAnalytics
} from './loaders';
import { commitNewInvestmentsBYOP, loadLoginData } from '../shared/loaders';
import {
  commitProfileData,
  commitReferrerForm,
  loadTermsForSubmission,
  registerAndLoginUser,
  commitShoppingFunds,
  loadMoneyMarketData,
  commitMoneyMarketFund
} from '../../shared/loaders';
import { FlowNode } from '../../../core/types/flow';
import {
  accountTypes,
  contactTextFn,
  redirectToCLX,
  redirectToEnablement
} from '../../shared/utils';
import { updateJourneyProfile } from '../../return-journey/loaders';
import { registerFlow } from '../../../core/flow-register';
import ExistingAgreementsWarnModal from '../../../components/ExistingAgreementModals/ExistingAgreementsWarnModal';

export const nodes: Array<FlowNode> = [
  {
    route: 'landing',
    screen: TaxableChecklistScreen,
    load: loadLoginData,
    commit: commitShoppingFunds,
    next: {
      default: routeTo('profile')
    },
    flowProps: {
      displayStickyBanner: true,
      getContactText: contactTextFn(),
      accountType: accountTypes.UGMAUTMA,
      analytics: {
        pageName: 'account-info'
      }
    }
  },
  {
    route: 'profile',
    screen: TaxableProfileScreen,
    load: makeLoadProfileData(),
    form: [
      {
        name: 'firstName',
        required: true
      },
      {
        name: 'lastName',
        required: true
      },
      {
        name: 'primaryEmailAddress',
        required: true,
        validation: isValidEmail
      },
      {
        name: 'amount',
        required: true,
        validation: meetsMinimumInvestment
      }
    ],
    startsPersistingSensitive: true,
    commit: commitProfileData,
    next: {
      cases: [
        [chose('skipLogin'), routeTo('information')],
        [guestUserLoginRequired, openModal(GuestUserLoginModal)]
      ],
      default: routeTo('information')
    },
    flowProps: {
      getContactText: contactTextFn('taxableBasicPI'),
      analytics: {
        pageName: 'About'
      }
    }
  },
  {
    route: 'information',
    screen: PersonalInformationScreen,
    form: [
      {
        name: 'firstName',
        required: true
      },
      {
        name: 'lastName',
        required: true
      },
      {
        name: 'middleName'
      },
      {
        name: 'dob',
        required: true,
        validation: isValidDob
      },
      { name: 'citizenship', required: true },
      {
        name: 'ssn',
        required: true,
        validation: [isValidMaskedSSN]
      },
      { name: 'minorFirstName', required: true },
      { name: 'minorMiddleName' },
      { name: 'minorLastName', required: true },
      { name: 'minorDob', required: true, validation: meetsMinorAgeValidation },
      { name: 'minorCitizenship', required: true },
      {
        name: 'minorSsn',
        required: true,
        validation: [isValidSSN, isUniqueSSN]
      }
    ],
    commit: commitPersonalInformation,
    next: {
      cases: [
        [userValidationFailed, openModal(UserValidationModal)],
        [
          existingUserShouldGotoEnablement,
          openModal(makeForcedLoginModal(redirectToEnablement))
        ],
        [
          existingUserShouldGotoLogin,
          openModal(makeForcedLoginModal(redirectToCLX))
        ],
        [showUpdateToIClassModal, openModal(UpdateToIClassModal)],
        [existingAgreementsPresent, openModal(ExistingAgreementsWarnModal)]
      ],
      default: routeTo('contact/information')
    },
    flowProps: {
      getContactText: contactTextFn('taxablePICI'),
      analytics: {
        pageName: 'PersonalInformation'
      }
    }
  },
  {
    route: 'contact/information',
    screen: ContactInformationScreen,
    load: loadTermsForSubmission,
    form: [
      { name: 'residentialStreet1', required: true },
      { name: 'residentialStreet2' },
      { name: 'residentialZip', required: true },
      { name: 'residentialCity', required: true },
      { name: 'residentialState', required: true },
      { name: 'mailingStreet1', required: true },
      { name: 'mailingStreet2' },
      { name: 'mailingZip', required: true },
      { name: 'mailingCity', required: true },
      { name: 'mailingState', required: true },
      { name: 'primaryEmailAddress', required: true },
      { name: 'phone1', required: true },
      { name: 'phone2' },
      { name: 'phone3' },
      { name: 'minorFirstName' },
      { name: 'minorLastName' },
      { name: 'mailingAddressDifferent', type: 'checkbox' },
      { name: 'minorSameAddress', type: 'checkbox' },
      { name: 'minorStreet1', required: true },
      { name: 'minorStreet2' },
      { name: 'minorZip', required: true },
      { name: 'minorCity', required: true },
      { name: 'minorState', required: true },
      { name: 'minorGoverningState', required: true },
      { name: 'useResidentialAddress', required: true },
      { name: 'useMailingAddress', required: true }
    ],
    commit: commitContactInformation,
    next: {
      cases: [
        [chosenAddressValidation, openModal(AddressValidationModal)],
        [isReviewMode(), routeTo('review')],
        [compose(hasURLTickers, isAuthenticated()), routeTo('byop/review')],
        [isAuthenticated(), routeTo('investment')]
      ],
      default: routeTo('credentials')
    },
    flowProps: {
      getContactText: contactTextFn('taxablePICI'),
      analytics: {
        pageName: 'ContactInformation'
      }
    }
  },
  {
    route: 'credentials',
    form: [
      {
        name: 'username',
        required: true,
        validation: usernameValidation
      },
      {
        name: 'password',
        required: true,
        validation: passwordValidation
      }
    ],
    screen: CreateCredentialsScreen,
    commit: registerAndLoginUser,
    next: {
      cases: [
        [isUsernameAlreadyTaken, openModal(CredentialsValidationModal)],
        [hasURLTickers, routeTo('byop/review')]
      ],
      default: routeTo('investment')
    },
    flowProps: {
      getContactText: contactTextFn('taxableCredentials'),
      analytics: {
        pageName: 'Enablement'
      }
    }
  },
  {
    route: 'investment',
    screen: InvestmentOptionsScreen,
    next: {
      cases: [[chose('money-market'), routeTo('money-market')]],
      default: routeTo('byop')
    },
    flowProps: {
      getContactText: contactTextFn('taxableBasicPI'),
      layoutClass: 'wide',
      title: 'How would you like to choose funds for your UGMA/UTMA account?',
      options: InvestmentOptionsContents,
      analytics: {
        pageName: 'TaxableInvestmentOptions'
      }
    }
  },
  {
    route: 'money-market',
    screen: MoneyMarketScreen,
    load: loadMoneyMarketData,
    commit: commitMoneyMarketFund,
    next: {
      cases: [[chose('viewAllMoneyMarketFunds'), routeTo('byop')]],
      default: routeTo('byop/review')
    },
    flowProps: {
      getContactText: contactTextFn('taxableBYOP'),
      layoutClass: 'medium',
      analytics: {
        pageName: 'MoneyMarketFund'
      }
    }
  },
  {
    route: 'byop',
    screen: BYOPScreen,
    commit: commitNewInvestmentsBYOP,
    next: {
      cases: [
        [showUpdateToIClassModal, openModal(UpdateToIClassModal)],
        [choseResultingPath('updateAndBack'), routeBack()]
      ],
      default: routeTo('byop/review')
    },
    flowProps: {
      fundMinimum: 1000,
      getContactText: contactTextFn('taxableBYOP'),
      layoutClass: 'medium',
      analytics: {
        pageName: 'BuildPortfolio'
      }
    }
  },
  {
    route: 'byop/review',
    screen: InvestmentReviewScreen,
    commit: commitNewInvestmentsBYOP,
    next: {
      cases: [
        [showUpdateToIClassModal, openModal(UpdateToIClassModal)],
        [choseResultingPath('updateAndBack'), routeBack()],
        [choseResultingPath('reselect-funds'), routeTo('byop')],
        [isReviewMode(), routeTo('review')]
      ],
      default: routeTo('review')
    },
    flowProps: {
      fundMinimum: 1000,
      getContactText: contactTextFn('taxableBYOP'),
      layoutClass: 'wide',
      analytics: {
        pageName: 'BuildPortfolioSummary'
      }
    }
  },
  {
    route: 'review',
    screen: ReviewScreen,
    load: loadTermsForSubmission,
    commit: createUGMAUTMAApplication,
    next: {
      cases: [
        [chose('updateCustomer'), partiallyUpdate()],
        [chose('byop'), routeTo('byop')],
        [chose('info'), routeTo('information')],
        [chose('minor'), routeTo('information')]
      ],
      default: routeTo('confirmation')
    },
    form: [
      {
        name: 'eligibleForTaxWithholding'
      }
    ],
    flowProps: {
      fundMinimum: 1000,
      renderTaxpayerCertifications: true,
      displayCombinedInformation: true,
      getContactText: contactTextFn('taxableReview'),
      analytics: {
        pageName: 'ReviewConfirm'
      }
    }
  },
  {
    route: 'confirmation',
    screen: ConfirmationScreen,
    commit: commitReferrerForm,
    next: {
      cases: [[chose('referrer'), partiallyUpdate()]],
      default: finish()
    },
    flowProps: {
      analytics: {
        pageName: 'Confirmation',
        isComplete: true
      }
    }
  }
];

export default registerFlow({
  namespace: `mutual-fund/general-investing/ugma-utma`,
  nodes,
  start: 'landing',
  pageDecorators: [BaseLayout, SummitRewards],
  onLoad: updateAnalytics,
  onCommit: updateJourneyProfile,
  flowProps: {
    getContactText: contactTextFn(),
    header: FlowHeader,
    footer: FooterSite
  }
});
